import type { TipoDeAula } from './api.js';

/**
 * ── O DESENHO DO TIPO DE AULA, E O DO CADEADO ────────────────────────────────
 *
 * Nove tipos declarados em `TipoDeAula` (o mesmo `LessonKind` da 0085), e um
 * desenho para cada — mais o cadeado, que não é tipo: é estado.
 *
 * ## Por que desenho, se o rótulo em texto já existia
 *
 * Porque o rótulo em texto responde à pergunta *depois* que a pessoa lê a
 * linha, e a pergunta se faz *enquanto* ela percorre a lista. `rotuloDoTipo`
 * escreve «Vídeo» num cinza de 12px ao lado de «14 min»: para saber que aquela
 * aula é um PDF é preciso parar em cada linha e ler. Uma silhueta numa coluna
 * fixa é lida sem fixação — o olho a percorre e só para quando ela muda.
 *
 * O texto **fica**. Desenho sozinho não é achável por busca, não é lido por
 * leitor de tela e não sobrevive a quem não distingue formas pequenas; texto
 * sozinho não é percorrível. Os dois juntos custam uma linha e resolvem os dois
 * lados — que é o motivo de `GlifoDoTipo` nascer `aria-hidden`: quem não vê o
 * desenho já tem a palavra a 5px dele, e anunciar as duas coisas seria uma
 * repetição.
 *
 * ## Por que à mão, e não uma biblioteca
 *
 * Pelo mesmo motivo que `index.css` não tem biblioteca de animação e que
 * `a-entrada-nao-paga-fonte-nem-icone` existe: V13 orça o pedaço de entrada do
 * aluno e a folga é de kilobytes, não de dezenas. Dez traços em `d` custam
 * centenas de bytes; um pacote de ícones custa o orçamento inteiro. E o
 * catálogo do core (`area-icones-da-entrada`) é outro objeto — ele nomeia as
 * estruturas da porta, não os tipos de aula.
 *
 * ## A LEI: o desenho sai do DADO, nunca da POSIÇÃO
 *
 * Este objeto é indexado por `kind`. Nunca por índice na lista. A casa já se
 * queimou com um selo escolhido por `EMOJI[i % EMOJI.length]`, onde `i` era a
 * posição: o mesmo mérito trocava de figura conforme a ordem em que vinha, e
 * nada ficava vermelho. `a-sala-diz-o-que-e-cada-linha` prende isto embaralhando
 * a lista e exigindo que cada aula mantenha o seu desenho.
 *
 * (O NÚMERO do módulo — «Módulo 3», «Submódulo 3.2» — é outra coisa e pode vir
 * da posição, porque é literalmente o que ele significa: uma coordenada no
 * sumário. Coordenada vem de posição; categoria, jamais.)
 *
 * ## Por que `embed` divide o desenho com `video`
 *
 * Porque `ROTULO_DO_TIPO` já diz «Vídeo» para os dois: para quem assiste, um
 * vídeo hospedado fora é um vídeo. Palavra igual com desenho diferente seria a
 * tela contradizendo a si mesma. É a única silhueta repetida, e a cerca exige
 * que continue sendo a única.
 */
const TRACO: Record<TipoDeAula, string> = {
  /* O triângulo cheio: a única figura sólida do conjunto, e a mais comum. */
  video: 'M5 3.5v9l7.5-4.5z',
  embed: 'M5 3.5v9l7.5-4.5z',
  /* Três linhas de comprimentos diferentes — um parágrafo, não uma lista. */
  texto: 'M3 4h10M3 8h10M3 12h6',
  /* A folha com o canto dobrado. */
  pdf: 'M4 2h5l3.5 3.5V14H4zM9 2v3.5h3.5',
  /* Quatro barras de alturas diferentes: uma onda, e nunca um gráfico. */
  audio: 'M3 6.5v3M6 3.5v9M9 5v6M12.5 7v2',
  /* O ponto que emite, e as duas ondas em volta. */
  live: 'M4.4 4.4a5 5 0 000 7.2M11.6 4.4a5 5 0 010 7.2',
  /*
   * A caixa com o tique — e a caixa é QUADRADA de propósito.
   *
   * A marca de conclusão da lista é um disco. Um tique dentro de um círculo
   * aqui seria a mesma figura em duas colunas dizendo coisas diferentes ("esta
   * aula é uma avaliação" ao lado de "esta aula está concluída"). O quadrado é
   * categoricamente outro, e a 15px a diferença círculo/quadrado é o que
   * sobrevive melhor.
   */
  quiz: 'M5.5 8.2l1.8 1.8 3.2-3.6',
  /* Moldura, sol e horizonte — o retrato, e não "duas folhas empilhadas", que
     colidiria com o baralho. */
  galeria: 'M3 11l3.2-3 3 2.6 1.8-1.6L14 11.5',
  /* Duas cartas deslocadas: o baralho que se vira. */
  flashcards: 'M11 13.5H4a1.5 1.5 0 01-1.5-1.5V5',
};

/** As partes que não são um `path` só — desenhadas por cima do traço acima. */
const EXTRA: Partial<Record<TipoDeAula, string>> = {
  live: 'circulo-cheio',
  quiz: 'caixa',
  galeria: 'moldura',
  flashcards: 'carta',
};

/** O cadeado. Não é tipo: ocupa a coluna do ESTADO, no lugar do disco. */
const CADEADO = 'M5.5 7V5a2.5 2.5 0 015 0v2';

function Moldura({
  tamanho,
  children,
}: {
  tamanho: number;
  children: React.ReactNode;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      {children}
    </svg>
  );
}

/**
 * A silhueta do tipo da aula, em `currentColor`.
 *
 * `currentColor` e não uma variável do tema: o desenho herda a cor de quem o
 * contém, então ele acompanha a ficha esmaecida, o título forte e a linha
 * trancada sem que ninguém escolha uma cor aqui. É o que faz o glifo sobreviver
 * a QUALQUER paleta que o tutor escolha — ele não tem paleta própria.
 *
 * `data-vst-tipo` é o que a cerca lê: sem ele, provar "o desenho saiu do dado"
 * exigiria comparar strings de `d` na mão.
 */
export function GlifoDoTipo({
  kind,
  tamanho = 15,
  simulado = false,
}: {
  kind: string | null | undefined;
  tamanho?: number;
  /**
   * ── A CAIXA TRACEJADA: a espécie dita em FORMA (0431) ────────────────────
   *
   * O simulado é a MESMA figura da avaliação — a caixa com o visto dentro —
   * com o contorno interrompido. Não é um segundo desenho a decorar: é a
   * gramática que esta casa já usa, onde o que muda de espécie muda de traço e
   * não de cor. Em cinzento, com daltonismo, num print em preto e branco, a
   * caixa aberta continua a ler-se como «isto não fecha nada» — e fechar é
   * exactamente o que o simulado não faz.
   *
   * Só o CONTORNO se interrompe; o visto continua contínuo. Tracejar os dois
   * daria uma silhueta que já não se reconhece como a irmã da prova, e o que
   * se quer dizer é «a mesma coisa, sem a consequência».
   */
  simulado?: boolean;
}) {
  /* A prévia do Estúdio monta a aula à mão e pode não mandar `kind` — a mesma
     reserva que `rotuloDoTipo` faz, e pelo mesmo motivo. Sem desenho é melhor
     que um desenho errado: a palavra ao lado já diz «Aula». */
  const k = (kind ?? '') as TipoDeAula;
  const traco = TRACO[k];
  if (!traco) return null;

  const extra = EXTRA[k];
  const treino = simulado && k === 'quiz';
  return (
    /* A cerca lê a ESPÉCIE aqui, e não o `kind`: `data-vst-tipo="quiz"` numa
       caixa tracejada e numa inteira não distinguiria as duas. */
    <span data-vst-tipo={treino ? 'simulado' : k} className="grid shrink-0 place-items-center">
      <Moldura tamanho={tamanho}>
        {extra === 'caixa' && (
          <rect
            x="2.5"
            y="2.5"
            width="11"
            height="11"
            rx="2.5"
            /* 2,6 de traço e 2,2 de folga: medido no glifo de 14px, é o maior
               passo que ainda desenha um traço em cada lado curto do canto —
               abaixo disso o tracejado some a 14px, acima vira pontilhado. */
            strokeDasharray={treino ? '2.6 2.2' : undefined}
          />
        )}
        {extra === 'moldura' && (
          <>
            <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
            <circle cx="5.6" cy="6.6" r="1.1" />
          </>
        )}
        {extra === 'carta' && <rect x="4.5" y="2.5" width="9" height="9" rx="1.5" />}
        {extra === 'circulo-cheio' && <circle cx="8" cy="8" r="1.8" fill="currentColor" stroke="none" />}
        <path d={traco} fill={k === 'video' || k === 'embed' ? 'currentColor' : 'none'} stroke={k === 'video' || k === 'embed' ? 'none' : 'currentColor'} />
      </Moldura>
    </span>
  );
}

/**
 * O cadeado, para a coluna do ESTADO.
 *
 * Existe porque a aula trancada desenhava um círculo vazado — a MESMA figura da
 * aula não assistida. `MarcaDoEstado` recebia `bloqueada` e só a usava no texto
 * do leitor de tela: quem enxerga não tinha sinal nenhum, e a única pista era a
 * frase laranja embaixo. Quatro estados pedem quatro formas.
 *
 * É também o assento do portão de pré-requisito que vem a seguir: uma aula
 * trancada por nota, por avaliação do módulo anterior ou por curso de trilha
 * chega aqui como `bloqueada` com outra `frase`, e a linha já está desenhada.
 */
export function GlifoDeCadeado({ tamanho = 15 }: { tamanho?: number }) {
  return (
    <span data-vst-tipo="cadeado" className="grid shrink-0 place-items-center">
      <Moldura tamanho={tamanho}>
        <rect x="3" y="7" width="10" height="6.5" rx="1.5" />
        <path d={CADEADO} />
      </Moldura>
    </span>
  );
}

/** Só para a cerca: os tipos que têm desenho, e o desenho de cada um. */
export const TRACO_DO_TIPO: Readonly<Record<TipoDeAula, string>> = TRACO;
